import type { Todo } from "../domain/entities.ts";
import { listEntities } from "../infrastructure/storage/indexed-db.ts";
import { incompleteTaskDependencies } from "../features/tasks/productivity.ts";
import { createTaskListReorderController } from "../features/tasks/task-list-reorder-controller.ts";
import { createBoardReorderWorkflowController } from "../features/tasks/board-reorder-workflow-controller.ts";
import { createTaskInteractionRuntime } from "../features/tasks/task-interaction-runtime.ts";
import type { TaskMutationController } from "../features/tasks/task-mutation-controller.ts";
import { taskUndoSnapshot, type TaskUndoField, type TaskUndoSnapshot } from "../features/tasks/task-undo-controller.ts";
import type { TaskViewState } from "../features/tasks/task-view-state.ts";
import type { TaskSelectionState } from "../features/tasks/task-selection-state.ts";
import type { ShellDataState } from "./shell-data-state.ts";
import type { SessionState } from "./session-state.ts";

export interface TaskInteractionCompositionDependencies {
  document: Document;
  window: Window;
  taskView: TaskViewState;
  taskSelection: TaskSelectionState;
  shellDataState: ShellDataState;
  sessionState: SessionState;
  reorderingEnabled(): boolean;
  canEdit(id?: string): boolean;
  mutate: TaskMutationController["mutate"];
  complete: TaskMutationController["complete"];
  myDay: { move(id: string, direction: -1 | 1): Promise<void>; reorder(ids: string[]): Promise<void> };
  createNextOccurrence(todo: Todo): Promise<unknown>;
  boardWipExceeded(todo: Todo): boolean;
  ensureBoardTaskVisible(collectionId: string, column: string, index: number): void;
  render(full?: boolean): Promise<void>;
  taskUndo(message: string, rootId: string, before: TaskUndoSnapshot[], fields: TaskUndoField[]): void;
  actionUndo(message: string, undo: () => void | Promise<void>): void;
  localUndo(message: string, undo: () => void): void;
  sync(): void;
  toast(message: string): void;
  hapticFeedback(): void;
  gestureStartsOnControl(target: EventTarget | null): boolean;
}

export function createTaskInteractionComposition(deps: TaskInteractionCompositionDependencies) {
  const { taskView, taskSelection, shellDataState, sessionState } = deps;
  const taskListReordering = createTaskListReorderController<TaskUndoSnapshot, TaskUndoField>({
    enabled: deps.reorderingEnabled, editable: () => deps.canEdit(), myDay: () => taskView.view === "myday", tasks: () => shellDataState.todos, rootId: () => sessionState.activeRootId,
    snapshot: taskUndoSnapshot, mutate: deps.mutate, moveMyDay: deps.myDay.move, reorderMyDay: deps.myDay.reorder,
    render: deps.render, undo: deps.taskUndo, toast: deps.toast, sync: deps.sync, hapticFeedback: deps.hapticFeedback,
  });
  const boardReordering = createBoardReorderWorkflowController<TaskUndoSnapshot, TaskUndoField>({
    editable: () => deps.canEdit(), tasks: () => shellDataState.todos, categories: () => shellDataState.categories, categoryId: () => sessionState.activeCollectionId, rootId: () => sessionState.activeRootId, mutate: deps.mutate,
    loadTask: async id => (await listEntities(sessionState.activeRootId!)).find(entity => entity.id === id)?.projection as Todo | undefined,
    createNextOccurrence: deps.createNextOccurrence, render: deps.render, wipExceeded: deps.boardWipExceeded, snapshot: taskUndoSnapshot,
    taskUndo: deps.taskUndo, actionUndo: deps.actionUndo, sync: deps.sync, hapticFeedback: deps.hapticFeedback,
    reducedMotion: () => deps.window.matchMedia("(prefers-reduced-motion: reduce)").matches, now: Date.now,
    ensureVisible: deps.ensureBoardTaskVisible,
    highlightMoved: id => {
      const card = Array.from(deps.document.querySelectorAll<HTMLElement>(".kanban-card")).find(item => item.dataset.todo === id);
      if (!card) return;
      card.classList.add("is-just-moved");
      deps.window.setTimeout(() => card.classList.remove("is-just-moved"), 1400);
    },
  });
  const taskInteractionRuntime = createTaskInteractionRuntime<TaskUndoSnapshot, TaskUndoField>({
    document: deps.document, listReordering: taskListReordering, boardReordering,
    rowSwipes: {
      enabled: () => deps.document.documentElement.dataset.taskSwipe === "on", selectionMode: () => taskSelection.active, tasks: () => shellDataState.todos,
      rootId: () => sessionState.activeRootId, canEdit: deps.canEdit, gestureStartsOnControl: deps.gestureStartsOnControl,
      blockers: todo => incompleteTaskDependencies(todo, shellDataState.todos), mutate: deps.mutate, complete: deps.complete, snapshot: taskUndoSnapshot,
      undo: deps.taskUndo, toast: deps.toast, hapticFeedback: deps.hapticFeedback, render: () => deps.render(false), sync: deps.sync,
    },
    hapticFeedback: deps.hapticFeedback, subtaskUndo: deps.localUndo,
  });
  return { taskListReordering, boardReordering, taskInteractionRuntime };
}
